import React, { useState } from 'react'
import { Box, ButtonBase, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import StorefrontIcon from '@mui/icons-material/Storefront'
import StarIcon from '@mui/icons-material/Star'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import BoltIcon from '@mui/icons-material/Bolt'
import GroupsIcon from '@mui/icons-material/Groups'
import { __t } from '../../../../i18n/t'
import { DataState, DiscountKind, ListOrigin, fmtN, isKindAvailable, recipes, showsRecipes, typeDisabledNote, typeInfo } from '../model'
import { useWiz } from '../state/WizardContext'
import { Callout, Rich } from '../ui/basics'
import { TextButton, cardHoverSx } from '../ui/controls'
import { C, TYPE_STYLE, focusRing } from '../ui/tokens'

const ICON = {
  [DiscountKind.default]: StorefrontIcon,
  [DiscountKind.special]: StarIcon,
  [DiscountKind.reward]: CardGiftcardIcon,
  [DiscountKind.recovery]: PersonSearchIcon,
}

// Estilo fora do JSX: o plugin de i18n embrulha literal de texto dentro de ternário e `&&` (ver ui/tokens.ts).
const gridSx = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 1.75 } as const
const recipeGridSx = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1.25 } as const
const typeHoverSx = { transform: 'translateY(-1px)', ...cardHoverSx } as const
const typeMotionSx = { transition: 'none', '&:hover': { transform: 'none' } } as const
const recipeSx = {
  textAlign: 'left', display: 'flex', gap: 1.25, alignItems: 'flex-start', justifyContent: 'flex-start', p: '12px 14px', minHeight: 44, borderRadius: '12px',
  border: `1px solid ${C.radioBorder}`, bgcolor: '#fff', '&:hover': cardHoverSx, '&.Mui-focusVisible': focusRing,
} as const

const Badge: React.FC<{ kind: DiscountKind; text: string; small?: boolean }> = ({ kind, text, small }) => (
  <Box
    component="span"
    sx={{
      alignSelf: 'flex-start', fontSize: small ? 11 : 11.5, fontWeight: 700, letterSpacing: '.04em', borderRadius: 999,
      px: small ? '7px' : 1, py: small ? '1px' : '2px', color: TYPE_STYLE[kind].ink, bgcolor: TYPE_STYLE[kind].tint,
    }}
  >
    {text}
  </Box>
)

const TypeCard: React.FC<{ kind: DiscountKind; compact?: boolean }> = ({ kind, compact }) => {
  const { d, c, locked, act } = useWiz()
  const info = typeInfo(kind)
  const st = TYPE_STYLE[kind]
  const on = d.kind === kind
  const disabled = !isKindAvailable(kind, d, c)
  const Icon = ICON[kind]
  const tile = compact ? 38 : 42
  return (
    <Box
      component="label"
      sx={{
        position: 'relative', display: 'flex', flexDirection: 'column', gap: 1.25, p: compact ? 2 : '18px', borderRadius: '16px',
        border: `2px solid ${on ? st.color : C.radioBorder}`, bgcolor: on ? st.tint : '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease, background-color .15s ease',
        '&:hover': disabled ? undefined : typeHoverSx,
        '&:has(input:focus-visible)': focusRing,
        '@media (prefers-reduced-motion: reduce)': typeMotionSx,
      }}
    >
      <input
        type="radio"
        name="tipo"
        checked={on}
        disabled={disabled}
        onChange={() => act.pickType(kind)}
        style={visuallyHidden as React.CSSProperties}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: tile, height: tile, borderRadius: '10px', bgcolor: on ? st.color : st.tint, opacity: disabled ? 0.55 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon aria-hidden sx={{ fontSize: compact ? 20 : 22, color: on ? '#fff' : st.color }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
          <Typography component="span" sx={{ fontSize: compact ? 15 : 16, fontWeight: 700, color: disabled ? C.muted : C.ink }}>{info.title}</Typography>
          <Badge kind={kind} text={info.badge} />
        </Box>
        {on
          ? <CheckCircleIcon aria-hidden sx={{ fontSize: 24, color: st.color, flexShrink: 0 }} />
          : <Box aria-hidden sx={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${C.inputBorder}`, flexShrink: 0 }} />}
      </Box>
      <Box component="span" sx={{ fontSize: compact ? 13.5 : 14, color: disabled ? C.muted : '#334155', lineHeight: 1.5 }}>{info.desc}</Box>
      {disabled ? <Box component="span" sx={{ fontSize: 12.5, color: C.muted, fontWeight: 500 }}>{typeDisabledNote(kind, locked)}</Box> : null}
    </Box>
  )
}

/** "Ou comece por um modelo pronto": cada modelo preenche os campos, o dono confere tudo antes de salvar. */
const Recipes: React.FC = () => {
  const { c, act, lists } = useWiz()
  // Rede que já carregou e não tem etiqueta nenhuma: o modelo "Só para uma etiqueta" não leva a lugar nenhum.
  const noTags = lists.tags.state === DataState.ready && c.tags.length === 0
  const shown = recipes().filter(r => r.id !== 'tag' || !noTags)
  return (
    <Box component="section" aria-labelledby="h-modelos" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, borderTop: `1px solid ${C.lineSoft}`, pt: 2.25 }}>
      <Typography id="h-modelos" component="h3" sx={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{__t('Ou comece por um modelo pronto')}</Typography>
      <Box sx={recipeGridSx}>
        {shown.map(r => (
          <ButtonBase key={r.id} onClick={() => act.applyRecipe(r.id)} focusRipple={false} sx={recipeSx}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: 'rgba(120,105,223,.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <BoltIcon aria-hidden sx={{ fontSize: 18, color: C.violet }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.375, minWidth: 0 }}>
              <Box component="span" sx={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{r.title}</Box>
              <Box component="span" sx={{ fontSize: 12.5, color: C.muted, lineHeight: 1.4 }}>{r.desc}</Box>
              <Badge kind={r.kind} text={typeInfo(r.kind).badge} small />
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  )
}

/** Etapa 1: o que o dono quer criar, em palavras de objetivo. Os dois tipos do dia a dia vêm na frente. */
/** Aviso de quem chegou com uma lista de clientes: só diz "Painel 360" se a lista veio de lá. */
function listArrival(origin: ListOrigin | null, n: number): string {
  if (origin === ListOrigin.rfm) {
    return n === 1
      ? __t('**1 cliente** escolhido no **Painel 360**: o desconto vai ser uma promoção só para ele.')
      : __t('**%{n} clientes** escolhidos no **Painel 360**: o desconto vai ser uma promoção só para eles.', { n: fmtN(n) })
  }
  return n === 1
    ? __t('**1 cliente** escolhido: o desconto vai ser uma promoção só para ele.')
    : __t('**%{n} clientes** escolhidos: o desconto vai ser uma promoção só para eles.', { n: fmtN(n) })
}

export const StepType: React.FC = () => {
  const { d, locked, maxStep } = useWiz()
  const [showOther, setShowOther] = useState(false)
  const otherOpen = showOther || d.kind === DiscountKind.reward || d.kind === DiscountKind.recovery
  return (
    <>
      {locked ? (
        <Callout tone="info" icon={<GroupsIcon />}>
          <Rich text={listArrival(d.listOrigin, d.customerIds.length)} />
        </Callout>
      ) : null}
      <Box id="f-type" role="radiogroup" aria-label={__t('Tipo de desconto')} sx={gridSx}>
        <TypeCard kind={DiscountKind.default} />
        <TypeCard kind={DiscountKind.special} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextButton
          onClick={() => setShowOther(!otherOpen)}
          icon={otherOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          expanded={otherOpen}
          controls={otherOpen ? 'outros-tipos' : undefined}
          sx={{ alignSelf: 'flex-start', fontSize: 14.5 }}
        >
          {__t('Outros tipos: Recompensa e Recuperação')}
        </TextButton>
        {otherOpen ? (
          <Box id="outros-tipos" role="radiogroup" aria-label={__t('Outros tipos de desconto')} sx={gridSx}>
            <TypeCard kind={DiscountKind.reward} compact />
            <TypeCard kind={DiscountKind.recovery} compact />
          </Box>
        ) : null}
      </Box>
      {showsRecipes(locked, maxStep) ? <Recipes /> : null}
    </>
  )
}
